import {
  CampaignProduct,
  CardType,
  DefaultMinBillingCycle,
  MultiSelectOption,
  ResolveAudienceRule,
  ResolveRuleCardTypeOptions,
  ResolveRuleChipLookups,
  ResolveRuleConditionFields,
  ResolveRuleConditionFormValue,
  ResolveRuleConditionLabels,
  ResolveRuleConditionOrder,
  ResolveRuleConditionType,
  ResolveRuleEmptyConditionFields,
  ResolveRuleFulfillmentOptions,
  ResolveRulePaymentTypeOptions,
  ResolveRuleThreeDsText,
  ResolveRuleTrafficSourceOptions,
  SessionSource,
  splitAudienceIds,
  TransactionPaymentSource,
} from '../../_models';

function hasValues(values: any[]): boolean {
  return !!values && values.length > 0;
}

function toIds<T extends number | string>(options: MultiSelectOption<number | string>[]): T[] {
  return (options || []).map((option) => option.id as T);
}

function toOptions(ids: (number | string)[], options: MultiSelectOption<number | string>[]): MultiSelectOption<number | string>[] {
  return (ids || []).map((id) => {
    const match = options.find((option) => String(option.id) === String(id));

    return match || { id, text: String(id) };
  });
}

export function getResolveRuleConditionTypes(rule: Partial<ResolveRuleConditionFields>): ResolveRuleConditionType[] {
  const present = {
    [ResolveRuleConditionType.FulfillmentType]: rule.fulfillment_type !== null && rule.fulfillment_type !== undefined,
    [ResolveRuleConditionType.BillingCycle]:
      !!rule.use_billing_cycle ||
      rule.min_billing_cycle > DefaultMinBillingCycle ||
      (rule.max_billing_cycle !== null && rule.max_billing_cycle !== undefined),
    [ResolveRuleConditionType.ThreeDs]: !!rule.is_3ds,
    [ResolveRuleConditionType.MerchantId]: hasValues(rule.merchant_ids),
    [ResolveRuleConditionType.CardType]: hasValues(rule.cc_types),
    [ResolveRuleConditionType.PaymentType]: hasValues(rule.payment_types),
    [ResolveRuleConditionType.Country]: hasValues(rule.countries),
    [ResolveRuleConditionType.State]: hasValues(rule.states),
    [ResolveRuleConditionType.Campaign]: !!rule.use_crm_campaign_filter && hasValues(rule.crm_campaigns),
    [ResolveRuleConditionType.Product]: !!rule.use_product_filter,
    [ResolveRuleConditionType.TrafficSource]: hasValues(rule.traffic_sources),
  };

  return ResolveRuleConditionOrder.filter((type) => present[type]);
}

export function buildResolveRuleConditionFields(
  types: ResolveRuleConditionType[],
  value: ResolveRuleConditionFormValue,
  products: CampaignProduct[]
): ResolveRuleConditionFields {
  const has = (type: ResolveRuleConditionType) => types.indexOf(type) !== -1;
  const fields: ResolveRuleConditionFields = { ...ResolveRuleEmptyConditionFields };

  if (has(ResolveRuleConditionType.FulfillmentType)) {
    fields.fulfillment_type = value.fulfillment_type;
  }

  if (has(ResolveRuleConditionType.BillingCycle)) {
    fields.min_billing_cycle = value.min_billing_cycle;
    fields.max_billing_cycle = value.max_billing_cycle;
    fields.use_billing_cycle = true;
  }

  if (has(ResolveRuleConditionType.ThreeDs)) {
    fields.is_3ds = true;
  }

  if (has(ResolveRuleConditionType.MerchantId) && splitAudienceIds(value.merchant_ids).length) {
    fields.merchant_ids = splitAudienceIds(value.merchant_ids);
  }

  if (has(ResolveRuleConditionType.CardType) && hasValues(value.cc_types)) {
    fields.cc_types = toIds<CardType>(value.cc_types);
  }

  if (has(ResolveRuleConditionType.PaymentType) && hasValues(value.payment_types)) {
    fields.payment_types = toIds<TransactionPaymentSource>(value.payment_types);
  }

  if (has(ResolveRuleConditionType.Country) && hasValues(value.countries)) {
    fields.countries = toIds<string>(value.countries);
  }

  if (has(ResolveRuleConditionType.State) && hasValues(value.states)) {
    fields.states = toIds<string>(value.states);
  }

  if (has(ResolveRuleConditionType.Campaign) && hasValues(value.crm_campaigns)) {
    fields.crm_campaigns = toIds<number>(value.crm_campaigns);
    fields.use_crm_campaign_filter = true;
  }

  if (has(ResolveRuleConditionType.Product)) {
    fields.use_product_filter = hasValues(products);
  }

  if (has(ResolveRuleConditionType.TrafficSource)) {
    fields.traffic_sources = toIds<SessionSource>(value.traffic_sources);
  }

  return fields;
}

export function buildResolveRuleConditionFormValue(
  rule: Partial<ResolveRuleConditionFields>,
  lookups: {
    countries: MultiSelectOption<number | string>[];
    states: MultiSelectOption<number | string>[];
    campaigns: MultiSelectOption<number | string>[];
  }
): ResolveRuleConditionFormValue {
  // countries are stored upper-case by the API but listed lower-case by RegionService
  const countryIds = (rule.countries || []).map((country) => country.toLowerCase());

  return {
    fulfillment_type: rule.fulfillment_type !== undefined ? rule.fulfillment_type : null,
    min_billing_cycle: rule.min_billing_cycle || DefaultMinBillingCycle,
    max_billing_cycle: rule.max_billing_cycle !== undefined ? rule.max_billing_cycle : null,
    merchant_ids: hasValues(rule.merchant_ids) ? rule.merchant_ids.join(', ') : null,
    cc_types: toOptions(rule.cc_types, ResolveRuleCardTypeOptions),
    payment_types: toOptions(rule.payment_types, ResolveRulePaymentTypeOptions),
    countries: toOptions(countryIds, lookups.countries),
    states: toOptions(rule.states, lookups.states),
    crm_campaigns: toOptions(rule.crm_campaigns, lookups.campaigns),
    traffic_sources: toOptions(rule.traffic_sources, ResolveRuleTrafficSourceOptions),
  };
}

export function isResolveRuleConditionIncomplete(
  type: ResolveRuleConditionType,
  value: ResolveRuleConditionFormValue,
  products: CampaignProduct[]
): boolean {
  let incomplete = false;

  switch (type) {
    case ResolveRuleConditionType.FulfillmentType:
      incomplete = value.fulfillment_type === null || value.fulfillment_type === undefined;
      break;
    case ResolveRuleConditionType.BillingCycle:
      incomplete = value.max_billing_cycle !== null && value.min_billing_cycle > value.max_billing_cycle;
      break;
    case ResolveRuleConditionType.MerchantId:
      incomplete = splitAudienceIds(value.merchant_ids).length === 0;
      break;
    case ResolveRuleConditionType.CardType:
      incomplete = !hasValues(value.cc_types);
      break;
    case ResolveRuleConditionType.PaymentType:
      incomplete = !hasValues(value.payment_types);
      break;
    case ResolveRuleConditionType.Country:
      incomplete = !hasValues(value.countries);
      break;
    case ResolveRuleConditionType.State:
      incomplete = !hasValues(value.states);
      break;
    case ResolveRuleConditionType.Campaign:
      incomplete = !hasValues(value.crm_campaigns);
      break;
    case ResolveRuleConditionType.Product:
      incomplete = !hasValues(products);
      break;
    case ResolveRuleConditionType.TrafficSource:
      incomplete = !hasValues(value.traffic_sources);
      break;
  }

  return incomplete;
}

function summarize(values: string[]): string {
  let summary = values.join(', ');

  if (values.length > 2) {
    summary = `${values.slice(0, 2).join(', ')} +${values.length - 2} more`;
  }

  return summary;
}

function describeBillingCycle(rule: Partial<ResolveRuleConditionFields>): string {
  const min = rule.min_billing_cycle || DefaultMinBillingCycle;
  let description = `${min} to ${rule.max_billing_cycle}`;

  if (rule.max_billing_cycle === null || rule.max_billing_cycle === undefined) {
    description = `${min} or greater`;
  } else if (rule.max_billing_cycle === min) {
    description = `${min}`;
  }

  return description;
}

// Sentence-cases a condition label for a chip ("Billing Cycle" -> "Billing cycle"), keeping acronyms such as "ID".
function chipLabel(type: ResolveRuleConditionType): string {
  return ResolveRuleConditionLabels[type]
    .split(' ')
    .map((word, index) => (index === 0 || word === word.toUpperCase() ? word : word.toLowerCase()))
    .join(' ');
}

export function getResolveRuleConditionChips(rule: ResolveAudienceRule, lookups: ResolveRuleChipLookups): string[] {
  const optionTexts = (ids: (number | string)[], options: MultiSelectOption<number | string>[]) =>
    toOptions(ids, options).map((option) => option.text);
  const countryIds = (rule.countries || []).map((country) => country.toLowerCase());
  // functions, so only the conditions the rule uses are read
  const chipValues: { [key in ResolveRuleConditionType]: () => string } = {
    [ResolveRuleConditionType.FulfillmentType]: () => optionTexts([rule.fulfillment_type], ResolveRuleFulfillmentOptions)[0],
    [ResolveRuleConditionType.BillingCycle]: () => describeBillingCycle(rule),
    [ResolveRuleConditionType.ThreeDs]: () => ResolveRuleThreeDsText,
    [ResolveRuleConditionType.MerchantId]: () => summarize(rule.merchant_ids),
    [ResolveRuleConditionType.CardType]: () => summarize(optionTexts(rule.cc_types, ResolveRuleCardTypeOptions)),
    [ResolveRuleConditionType.PaymentType]: () => summarize(optionTexts(rule.payment_types, ResolveRulePaymentTypeOptions)),
    [ResolveRuleConditionType.Country]: () => summarize(optionTexts(countryIds, lookups.countries)),
    [ResolveRuleConditionType.State]: () => summarize(rule.states),
    [ResolveRuleConditionType.Campaign]: () => summarize(optionTexts(rule.crm_campaigns, lookups.campaigns)),
    [ResolveRuleConditionType.Product]: () =>
      lookups.products.length ? summarize(lookups.products.map((product) => product.name)) : 'selected products',
    [ResolveRuleConditionType.TrafficSource]: () => summarize(optionTexts(rule.traffic_sources, ResolveRuleTrafficSourceOptions)),
  };

  return getResolveRuleConditionTypes(rule).map((type) => `${chipLabel(type)} is ${chipValues[type]()}`);
}
